import { SPACING } from "../../../../../utils/constants";
import {
  HoldingAssetGroup,
  HoldingColumn,
  HoldingColumnShare,
  HoldingSortKey,
  HoldingSortState,
} from "./types";

// One label per sort key, exhaustive by type, read by both the column headers and the
// mobile sort select — whose option order this declaration order also fixes.
export const HOLDING_SORT_LABELS: Record<HoldingSortKey, string> = {
  positionValue: "Broker value",
  unrealizedReturnValue: "Return",
  costBasis: "Cost basis",
  marketPrice: "Market price",
  quantity: "Quantity",
  instrument: "Instrument",
};

// Kebab width plus its own padding — global `box-sizing: border-box` means a
// declared width under `table-layout: fixed` must cover both, or the button spills past the cell.
const ACTIONS_COLUMN_WIDTH = SPACING.xl + SPACING.sm * 2;

// Render-order visual ratio of the proportional columns; what they sum to is derived below.
const PROPORTIONAL_COLUMNS: HoldingColumnShare[] = [
  {
    label: HOLDING_SORT_LABELS.instrument,
    share: 25,
    sortKey: "instrument",
  },
  {
    label: HOLDING_SORT_LABELS.quantity,
    share: 10,
    sortKey: "quantity",
  },
  {
    label: HOLDING_SORT_LABELS.marketPrice,
    share: 15,
    sortKey: "marketPrice",
  },
  {
    label: HOLDING_SORT_LABELS.costBasis,
    share: 14,
    sortKey: "costBasis",
  },
  {
    label: HOLDING_SORT_LABELS.positionValue,
    share: 16,
    sortKey: "positionValue",
  },
  {
    label: HOLDING_SORT_LABELS.unrealizedReturnValue,
    share: 14,
    sortKey: "unrealizedReturnValue",
  },
];

const SHARE_TOTAL = PROPORTIONAL_COLUMNS.reduce(
  (total, { share }) => total + share,
  0,
);

// Funds the fixed kebab column out of the proportional widths so all seven columns sum to 100%.
const proportionalColumnWidth = (share: number): string =>
  `calc(${(share / SHARE_TOTAL) * 100}% - ${(share / SHARE_TOTAL) * ACTIONS_COLUMN_WIDTH}px)`;

// Single source for the header row, column widths and body-cell alignment — separate
// lists used to drift out of sync when a column was added to one but not the others.
export const HOLDING_COLUMNS: HoldingColumn[] = [
  ...PROPORTIONAL_COLUMNS.map(({ share, ...column }) => ({
    ...column,
    width: proportionalColumnWidth(share),
  })),
  {
    label: "Actions",
    width: `${ACTIONS_COLUMN_WIDTH}px`,
    labelHidden: true,
  },
];

// Three deliberate spellings: the row and card render these from separate files, so
// duplicated wording would otherwise drift.
export const PRICE_OVERRIDE_ENTRY_LABEL = "Set my price for pre-2020 history";

// Note above already names the period, so this one only offers the remedy.
export const PRICE_OVERRIDE_BLOCKED_LABEL = "Set my price for this period";

export const PRICE_OVERRIDE_ACTION_LABEL = "Set my price…";
export const SOURCE_COUNTRY_ACTION_LABEL = "Set source country…";

// Not a dash: the value is known, it's the account's broker value that can't be completed.
export const ALLOCATION_UNAVAILABLE_LABEL = "Allocation unavailable";

export const INSTRUMENT_MARK_LETTERS = 2;

export const INSPECT_POSITION_HINT = "Inspect position";

// The mockup's value, kept deliberately — the pagination bar windows its numbers so a
// 500-position ceiling doesn't render a hundred of them.
export const HOLDINGS_PAGE_SIZE = 5;

// Biggest position first is the question this section is opened to answer.
export const DEFAULT_HOLDING_SORT: HoldingSortState = {
  key: "positionValue",
  dir: "desc",
};

export const ASSET_GROUP_LABELS: Record<HoldingAssetGroup, string> = {
  STOCK: "Stocks",
  ETF: "ETFs",
  OTHER: "Other (incl. crypto & FX)",
};

export const ALL_ASSETS_LABEL = "All instruments";

export const NEW_LEDGER_TITLE = "No holdings yet";
export const NEW_LEDGER_BODY =
  "Record the first trade to begin this brokerage account ledger.";
export const RECORD_FIRST_TRADE_LABEL = "Record first trade";

export const ALL_SOLD_TITLE = "Every position is closed";
export const ALL_SOLD_BODY =
  "Record a trade to open a new one, or switch to Closed to record a dividend or fee against a sold position.";
export const RECORD_TRADE_LABEL = "Record trade";

export const NO_MATCH_TITLE = "No holdings match";
export const NO_MATCH_BODY =
  "Adjust the search or remove a filter to return to the full brokerage account.";

export const CLEAR_FILTERS_LABEL = "Clear filters";
